import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { addReportIssue } from "../../redux/actions/action-creator";
import ReviewPopupFrame from "./ReviewPopupFrame";

import './ReviewPopup.css';


const ReportReasonModal = ({
  show,
  onClose,
  ordersDetail,
  onSubmitSuccess,
  issueList = [],
  issueLoading = false,
  issueFetchError = "",
}) => {
  const [comment, setComment] = useState('');
  const [selectReason, setSelectReason] = useState('');
  const [email, setEmail] = useState('');
  const [images, setImages] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const maxWords = 200;
  const dispatch = useDispatch();

  useEffect(() => {
    if (!show) {
      setImages((prevImages) => {
        prevImages.forEach((image) => {
          if (image.preview) {
            URL.revokeObjectURL(image.preview);
          }
        });
        return [];
      });
      setComment('');
      setSelectReason('');
      setEmail('');
      setErrors({});
      setSubmitting(false);
    }
  }, [show]);

  const getIssueLabel = (reason) =>
    reason?.value ||
    reason?.title ||
    reason?.name ||
    reason?.label ||
    reason?.reason ||
    "Report issue";

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const nextImages = files.slice(0, 1).map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    images.forEach((image) => {
      if (image.preview) {
        URL.revokeObjectURL(image.preview);
      }
    });

    setImages(nextImages);
    e.target.value = "";
  };

  const removeImage = (index) => {
    setImages((prev) => {
      const removedImage = prev[index];
      if (removedImage?.preview) {
        URL.revokeObjectURL(removedImage.preview);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const wordCount = comment.trim().split(/\s+/).filter(word => word.length > 0).length;

  const validateForm = () => {
    const nextErrors = {};

    if (!selectReason) {
      nextErrors.reported_issue = "Please select report issue.";
    }
    if (!email.trim()) {
      nextErrors.email = "Please enter email.";
    }
    if (!comment.trim()) {
      nextErrors.user_comments = "Please enter comment.";
    }
    if (wordCount > maxWords) {
      nextErrors.user_comments = `Comment can not be more than ${maxWords} words.`;
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || submitting) return;

    const bookingOrderId =
      ordersDetail?.booking_order_id ||
      ordersDetail?.bookingOrderId ||
      ordersDetail?.order_id ||
      ordersDetail?.id;

    if (!bookingOrderId) {
      toast.error("Order id not found.");
      return;
    }

    const formData = new FormData();
    formData.append("booking_order_id", bookingOrderId);
    formData.append("reported_issue", selectReason);
    formData.append("email", email.trim());
    formData.append("user_comments", comment.trim());

    if (images[0]?.file) {
      formData.append("image_1", images[0].file);
    }

    setSubmitting(true);

    try {
      const response = await dispatch(addReportIssue(formData));
      if (response?.result === true || response?.result === 1 || response?.result === "1") {
        toast.success(response?.message || "Report issue submitted successfully.");
        if (onSubmitSuccess) {
          await onSubmitSuccess(response);
        }
        onClose();
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        error?.message ||
        "Unable to submit report issue."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <ReviewPopupFrame title="Report an Issue" onClose={onClose} busy={submitting}>
          {/* Body */}
          <div className="review-popup-body">
            <div className="review-input-group">
              <label className="review-label">Email</label>
              <input
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrors((prev) => ({ ...prev, email: "" }));
                }}
                placeholder="Enter email"
                className="review-textarea"
                required
              />
              {errors.email && (
                <small className="text-danger">{errors.email}</small>
              )}
            </div>

            <div className="review-input-group">
              <label className="review-label">Report Issue</label>
              <select
                value={selectReason}
                onChange={(e) => {
                  setSelectReason(e.target.value);
                  setErrors((prev) => ({ ...prev, reported_issue: "" }));
                }}
                className="review-select"
                required
              >
                <option value="">
                  {issueLoading ? "Loading reasons..." : "Select reason"}
                </option>
                {!issueLoading && issueList.map((reason) => (
                  <option
                    key={reason?.id || reason?.value || reason?.title || reason?.name}
                    value={reason?.id || reason?.value || reason?.title || reason?.name}
                  >
                    {getIssueLabel(reason)}
                  </option>
                ))}
              </select>
              {issueFetchError && (
                <small className="text-danger">{issueFetchError}</small>
              )}
              {errors.reported_issue && (
                <small className="text-danger">{errors.reported_issue}</small>
              )}
            </div>

            {/* Review Textarea */}
            <div className="review-input-group">
              <label className="review-label">User Comments</label>
              <textarea
                value={comment}
                onChange={(e) => {
                  setComment(e.target.value);
                  setErrors((prev) => ({ ...prev, user_comments: "" }));
                }}
                placeholder="Type your comment"
                className="review-textarea"
                rows="3"
                required
              />
              {errors.user_comments && (
                <small className="text-danger">{errors.user_comments}</small>
              )}
              <div className="review-word-count">
                <span className={wordCount > maxWords ? 'review-word-count-over' : ''}>
                  ~{wordCount > maxWords ? maxWords : wordCount} Words
                </span>
              </div>
            </div>

            {/* Image Upload Section */}
            <div className="review-upload-section">
              <label className="review-label">Upload Images (Optional)</label>
              
              <div className="review-images-container">
                <div className="review-images-grid">
                  {images.map((img, index) => (
                    <div key={index} className="review-image-item">
                      <img src={img.preview} alt={`Upload ${index + 1}`} />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="review-image-remove"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  
                  <label className="review-upload-box">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="17 8 12 3 7 8"></polyline>
                      <line x1="12" y1="3" x2="12" y2="15"></line>
                    </svg>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </div>
              
            </div>
          </div>

          {/* Footer */}
          <div className="review-popup-footer">
            <button className="review-btn review-btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button
              className="review-btn review-btn-submit"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>
      </ReviewPopupFrame>
      <ToastContainer position="top-right" autoClose={2500} />
    </>
  );
};

export default ReportReasonModal;
